import React from 'react';

import { PermissionSwitch } from './PermissionSwitch';
import type { FrontendPermissionId } from '@personnel/domain/types/PermissionsConst';
import type { IconName } from './DynamicIcon';

/**
 * Permission Module Component
 *
 * Container with a parent switch that controls the enable/disable state of child permissions.
 * When the parent switch is OFF, all child permissions are disabled.
 *
 * Labels come from i18n via the parent component (PermissionsRenderer).
 *
 * @param {PermissionModuleProps} props - Component properties
 * @returns {React.ReactElement} Permission module component
 *
 * @example
 * <PermissionModule
 *   parentSwitchId="store-pos-cash-module"
 *   parentSwitchLabel="Módulo de Cajas"
 *   parentSwitchDescription="Permite acceder al módulo de cajas"
 *   parentBackendIds={['areaTable.canViewCashFlow']}
 *   parentChecked={true}
 *   onParentChange={(checked) => console.log(checked)}
 *   iconName="computer"
 * >
 *   <PermissionCheckbox ... />
 *   <PermissionCheckbox ... />
 * </PermissionModule>
 */

interface PermissionModuleProps {
  /** Icon name (optional) */
  iconName?: IconName;
  /** Parent switch permission ID */
  parentSwitchId: FrontendPermissionId;
  /** Parent switch label (from i18n) */
  parentSwitchLabel: string;
  /** Parent switch description (from i18n, optional) */
  parentSwitchDescription?: string;
  /** Parent switch backend IDs */
  parentBackendIds: readonly string[];
  /** Whether the parent switch is checked */
  parentChecked: boolean;
  /** Callback when parent switch changes */
  onParentChange: (checked: boolean) => void;
  /** Whether the module is disabled (from gatekeeper or global disabled) */
  disabled?: boolean;
  /** Child permissions */
  children: React.ReactNode;
}

const styles = {
  container: 'flex flex-col gap-3',
  parentSwitch: 'mb-2',
  content: {
    wrapper: 'flex flex-col gap-2 ml-6',
    disabled: 'opacity-50 pointer-events-none',
  },
};

export const PermissionModule = ({
  iconName,
  parentSwitchId,
  parentSwitchLabel,
  parentSwitchDescription,
  parentBackendIds,
  parentChecked,
  onParentChange,
  disabled = false,
  children,
}: PermissionModuleProps): React.ReactElement => {
  // Children are disabled when: module is disabled OR parent switch is OFF
  const childrenDisabled = disabled || !parentChecked;

  return (
    <div className={styles.container}>
      {/* Parent Switch with icon */}
      <div className={styles.parentSwitch}>
        <PermissionSwitch
          backendIds={parentBackendIds}
          checked={parentChecked}
          description={parentSwitchDescription}
          disabled={disabled}
          iconName={iconName}
          label={parentSwitchLabel}
          onChange={onParentChange}
          permissionId={parentSwitchId}
          size="small"
        />
      </div>

      {/* Children Permissions (disabled when module is disabled OR parent is OFF) */}
      <div className={`${styles.content.wrapper} ${childrenDisabled ? styles.content.disabled : ''}`}>{children}</div>
    </div>
  );
};
